import { rootApi } from '../rootApi';

export const dealerPartsReturnProcessApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getDealerStockList: builder.query({
            query: (params = {}) => ({
                url: 'v1/dealerpartsreturnprocess/dealer-stock-list',
                method: 'POST',
                body: {
                    tabStatus: params.tabStatus ?? null,
                    domesticType: params.domesticType ?? null,
                    languageId: params.languageId,
                    pageNumber: params.pageNumber,
                    pageSize: params.pageSize,
                    filters: params.filters ?? null,
                    sorts: params.sorts ?? null,
                    includeFilterOptions: params.includeFilterOptions ?? false,
                    filterOptionsColumns: params.filterOptionsColumns ?? null
                }
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && response.data.pagedResult) {
                        return {
                            ...response.data.pagedResult,
                            tabCounts: response.data.tabCounts ?? null,
                            filterOptions: response.data.filterOptions ?? null
                        };
                    }

                    if (response.data && Array.isArray(response.data.rows)) {
                        return {
                            ...response.data,
                            filterOptions: response.data.filterOptions ?? null
                        };
                    }

                    if (Array.isArray(response)) {
                        return { rows: response, totalCount: response.length, pageItemCount: response.length, filterOptions: null };
                    }
                    return { rows: [], totalCount: 0, pageItemCount: 0, tabCounts: null, filterOptions: null };
                }

                console.warn('⚠️ Response structure not as expected, returning empty array');
                return { rows: [], totalCount: 0, pageItemCount: 0, filterOptions: null };
            },
            providesTags: ['DealerPartsReturnProcess']
        }),
        createReturnRequest: builder.mutation({
            query: (data) => {
                const formData = new FormData();

                // Add main fields
                formData.append('Item.PartCode', data.partCode || '');
                formData.append('Item.Uom', data.uom);
                formData.append('Item.SelectedReasonType', data.selectedReasonType || '');
                formData.append('Item.ReturnDescription', data.returnDescription || '');
                formData.append('Item.IsFaultyPart', data.isFaultyPart);
                formData.append('Item.Quantity', data.quantity);
                formData.append('Item.LineDescription', data.lineDescription || '');
                formData.append('Item.Reason', data.reason || '');
                if (data.invoiceNo) formData.append('Item.InvoiceNo', data.invoiceNo);

                // Add optional fields for faulty parts
                if (data.isFaultyPart) {
                    if (data.vinNo) formData.append('Item.VinNo', data.vinNo);
                    if (data.assemblyDate) formData.append('Item.AssemblyDate', data.assemblyDate);
                    if (data.assemblyKm) formData.append('Item.AssemblyKm', data.assemblyKm);
                    if (data.faultDate) formData.append('Item.FaultDate', data.faultDate);
                    if (data.faultKm) formData.append('Item.FaultKm', data.faultKm);
                }

                // Add files
                if (data.files && data.files.length > 0) {
                    data.files.forEach((file, index) => {
                        formData.append('Item.Files', file);
                    });
                }

                return {
                    url: 'v1/dealerpartsreturnprocess/return-requests/create',
                    method: 'POST',
                    body: formData
                };
            },
            transformResponse: (response) => {
                // Backend'den gelen standard response format:
                // { isSuccessful: true, httpStatusCode: 200, returnUrl: null, count: 1, errorMessage: null, data: true }
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data,
                            count: response.count
                        };
                    } else if (response.isSuccessful === false) {
                        // Backend error case
                        console.error('❌ Backend returned error:', response.errorMessage);
                        throw new Error(response.errorMessage || 'API_CREATE_ERROR');
                    }
                }

                // Fallback for unexpected response structure
                console.warn('⚠️ Unexpected response structure, using fallback');
                return {
                    success: true,
                    data: response
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                // Detaylı error bilgisi döndür
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_GENERAL_ERROR'
                };
            },
            invalidatesTags: ['DealerPartsReturnProcess']
        }),
        sendToRegionalManager: builder.mutation({
            query: (id) => ({
                url: `v1/dealerpartsreturnprocess/${id}/submit`,
                method: 'POST'
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data
                        };
                    } else if (response.isSuccessful === false) {
                        throw new Error(response.errorMessage || 'API_SEND_TO_RM_ERROR');
                    }
                }

                return {
                    success: true,
                    data: response
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_GENERAL_ERROR'
                };
            },
            invalidatesTags: ['DealerPartsReturnProcess']
        }),
        cancelReturnRequest: builder.mutation({
            query: (id) => ({
                url: `v1/dealerpartsreturnprocess/return-requests/${id}/cancel`,
                method: 'POST'
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data
                        };
                    } else if (response.isSuccessful === false) {
                        throw new Error(response.errorMessage || 'API_CANCEL_ERROR');
                    }
                }

                return {
                    success: true,
                    data: response
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_GENERAL_ERROR'
                };
            },
            invalidatesTags: ['DealerPartsReturnProcess']
        }),

        approveReturnRequest: builder.mutation({
            query: (id) => ({
                url: `v1/dealerpartsreturnprocess/approval/${id}/approve`,
                method: 'POST'
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data
                        };
                    } else if (response.isSuccessful === false) {
                        throw new Error(response.errorMessage || 'API_APPROVE_ERROR');
                    }
                }

                return {
                    success: true,
                    data: response
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_GENERAL_ERROR'
                };
            },
            invalidatesTags: ['DealerPartsReturnProcess']
        }),

        rejectReturnRequest: builder.mutation({
            query: ({ id, rejectionReason }) => ({
                url: `v1/dealerpartsreturnprocess/approval/${id}/reject`,
                method: 'POST',
                body: { rejectionReason }
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data
                        };
                    } else if (response.isSuccessful === false) {
                        throw new Error(response.errorMessage || 'API_REJECT_ERROR');
                    }
                }

                return {
                    success: true,
                    data: response
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_GENERAL_ERROR'
                };
            },
            invalidatesTags: ['DealerPartsReturnProcess']
        }),

        getReturnProcessDocumentsById: builder.query({
            query: (returnRequestId) => ({
                url: 'v1/dealerpartsreturnprocess/getdocumentsbyid',
                method: 'GET',
                params: { id: returnRequestId }
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data || [],
                            count: response.count || 0
                        };
                    } else if (response.isSuccessful === false) {
                        throw new Error(response.errorMessage || 'API_FETCH_DOCUMENTS_ERROR');
                    }
                }

                // Fallback
                return {
                    success: true,
                    data: response?.data || [],
                    count: response?.count || 0
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_FETCH_DOCUMENTS_GENERAL_ERROR'
                };
            },
            providesTags: (result, error, returnRequestId) => [{ type: 'DealerPartsReturnProcess', id: `documents-${returnRequestId}` }]
        }),

        getPdfData: builder.query({
            query: (returnRequestId) => ({
                url: `v1/dealerpartsreturnprocess/${returnRequestId}/pdf-data`,
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.isSuccessful === true && response.httpStatusCode === 200) {
                        return {
                            success: true,
                            data: response.data
                        };
                    } else if (response.isSuccessful === false) {
                        throw new Error(response.errorMessage || 'API_FETCH_PDF_DATA_ERROR');
                    }
                }

                // Fallback
                return {
                    success: true,
                    data: response?.data || {}
                };
            },
            transformErrorResponse: (response, meta, arg) => {
                return {
                    status: response?.status || 'UNKNOWN_ERROR',
                    data: response?.data,
                    errorMessage: response?.data?.errorMessage || response?.data?.message || 'API_FETCH_PDF_DATA_GENERAL_ERROR'
                };
            }
        }),

        getReturnReasonTypes: builder.query({
            query: ({ languageId }) => ({
                url: 'v1/lookup/get',
                method: 'GET',
                params: { groupCode: 'REASON_ORDER_RETURN', languageId }
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data.map((item) => ({
                            value: parseInt(item.code, 10),
                            label: item.translatedText
                        }));
                    }

                    if (Array.isArray(response)) {
                        return response.map((item) => ({
                            value: parseInt(item.code, 10),
                            label: item.translatedText
                        }));
                    }
                }

                return [];
            }
        }),

        exportReturnProcessToExcel: builder.query({
            query: (params = {}) => ({
                url: 'v1/DealerPartsReturnProcess/ExportReturnProcessToExcel',
                method: 'POST',
                body: {
                    tabStatus: params.tabStatus ?? null,
                    domesticType: params.domesticType ?? null,
                    languageId: params.languageId,
                    filters: params.filters ?? null,
                    sorts: params.sorts ?? null
                }
            }),
            transformResponse: (response) => {
                const data = response?.data ?? {};
                return {
                    fileBase64: data.fileBase64,
                    fileName: data.fileName,
                    contentType: data.contentType,
                    recordCount: data.recordCount,
                    fileSize: data.fileSize
                };
            }
        })
    })
});

export const {
    useGetDealerStockListQuery,
    useCreateReturnRequestMutation,
    useSendToRegionalManagerMutation,
    useCancelReturnRequestMutation,
    useApproveReturnRequestMutation,
    useRejectReturnRequestMutation,
    useGetReturnProcessDocumentsByIdQuery,
    useLazyGetPdfDataQuery,
    useGetReturnReasonTypesQuery,
    useLazyGetReturnReasonTypesQuery,
    useLazyExportReturnProcessToExcelQuery
} = dealerPartsReturnProcessApi;
